import { useCallback, useEffect, useState } from "react";

import { Edit as EditIcon } from "@mui/icons-material";
import { Button, Chip, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { apiClient, statusColors, statusLabels, getErrorMessage } from "@/features/shared";
import { SearchTextField, useSnackbar } from "@/features/ui";
import {
  AddUnitOfMeasurementFormDialog,
  EditUnitOfMeasurementFormDialog,
} from "@/features/unit-of-measurements";
import { useSearchFilter, useServerPagination } from "@/hooks";

export const UnitOfMeasurements = () => {
  const { hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [unitOfMeasurementId, setUnitOfMeasurementId] = useState(null);
  const [unitOfMeasurements, setUnitOfMeasurements] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });

  const loadUnitOfMeasurements = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiClient.get("unit-of-measurements", {
        params: {
          ...paginationParams,
          ...(keyword && { name: keyword }),
        },
      });
      setUnitOfMeasurements(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat satuan ukuran"));
    } finally {
      setLoading(false);
    }
  }, [paginationParams, keyword, setRowCount, showSnackbar]);

  useEffect(() => {
    loadUnitOfMeasurements();
  }, [loadUnitOfMeasurements]);

  const toolbarContent = hasPermission(PermissionEnum.CREATE_UNIT_OF_MEASUREMENTS) && (
        <Button
          variant="contained"
          onClick={() => setIsAddDialogOpen(true)}
        >
          Tambah
        </Button>
      );

  return (
    <PageContainer maxWidth="xl" breadcrumbs={[]} slotProps={{ header: { slotProps: { toolbar: { children: toolbarContent } } } }}>
      <Stack spacing={3}>
        <Grid container sx={{ justifyContent: "flex-end" }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nama satuan"
              {...searchProps}
            />
          </Grid>
        </Grid>
        <DataGrid
          rows={unitOfMeasurements}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "name",
              headerName: "Nama",
              flex: 3,
              sortable: false,
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={statusLabels[row.status]}
                  color={statusColors[row.status]}
                />
              ),
            },
            ...(hasPermission(PermissionEnum.UPDATE_UNIT_OF_MEASUREMENTS)
              ? [
                  {
                    field: "actions",
                    type: "actions",
                    headerName: "Aksi",
                    getActions: (params) => [
                      <IconButton
                        key="edit"
                        onClick={() => setUnitOfMeasurementId(params.id)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>,
                    ],
                  },
                ]
              : []),
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
        <AddUnitOfMeasurementFormDialog
          isOpen={isAddDialogOpen}
          onClose={() => setIsAddDialogOpen(false)}
          onSubmitted={loadUnitOfMeasurements}
        />
        <EditUnitOfMeasurementFormDialog
          unitOfMeasurementId={unitOfMeasurementId}
          isOpen={!!unitOfMeasurementId}
          onClose={() => setUnitOfMeasurementId(null)}
          onSubmitted={loadUnitOfMeasurements}
        />
      </Stack>
    </PageContainer>
  );
};
